const API_BASE = import.meta.env.VITE_API_BASE ?? '/api/v1'

export interface ApiErrorBody {
  code: string
  message: string
  traceId?: string
  path?: string
  fieldErrors?: Array<{ field: string; message: string }>
}

export class ApiClientError extends Error {
  readonly status: number
  readonly code: string
  readonly traceId?: string
  readonly fieldErrors: ApiErrorBody['fieldErrors']

  constructor(status: number, body: ApiErrorBody) {
    super(body.message || '请求失败')
    this.name = 'ApiClientError'
    this.status = status
    this.code = body.code || 'REQUEST_FAILED'
    this.traceId = body.traceId
    this.fieldErrors = body.fieldErrors
  }
}

export function summarizeApiError(error: ApiClientError): string {
  const messages = [...new Set(
    (error.fieldErrors ?? [])
      .map((item) => item.message.trim())
      .filter(Boolean),
  )]
  if (!messages.length) return error.message
  if (messages.length <= 3) return messages.join('；')
  return `${messages.slice(0, 2).join('；')}；另有 ${messages.length - 2} 项需要修正`
}

interface CsrfPayload {
  headerName: string
  token: string
}

let csrfPayload: CsrfPayload | null = null

async function loadCsrf(): Promise<CsrfPayload> {
  if (csrfPayload) return csrfPayload
  const response = await fetch(`${API_BASE}/auth/csrf`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  if (!response.ok) {
    throw await toError(response)
  }
  csrfPayload = (await response.json()) as CsrfPayload
  return csrfPayload
}

export function clearCsrf(): void {
  csrfPayload = null
}

export async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase()
  const headers = new Headers(init.headers)
  headers.set('Accept', 'application/json')
  if (init.body && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json')
  }

  const needsCsrf = !['GET', 'HEAD', 'OPTIONS'].includes(method) && path !== '/auth/login'
  if (needsCsrf) {
    const csrf = await loadCsrf()
    headers.set(csrf.headerName, csrf.token)
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
    credentials: 'include',
  })

  if (!response.ok) {
    if (response.status === 401) clearCsrf()
    throw await toError(response)
  }
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T
  }
  return (await response.json()) as T
}

export async function apiDownload(path: string, fallbackFileName: string): Promise<void> {
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
  })
  if (!response.ok) {
    if (response.status === 401) clearCsrf()
    throw await toError(response)
  }
  const blob = await response.blob()
  const disposition = response.headers.get('content-disposition') ?? ''
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1]
  const plain = disposition.match(/filename="?([^";]+)"?/i)?.[1]
  let fileName = fallbackFileName
  try {
    fileName = encoded ? decodeURIComponent(encoded) : plain || fallbackFileName
  } catch {
    fileName = fallbackFileName
  }
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = fileName
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

async function toError(response: Response): Promise<ApiClientError> {
  let body: ApiErrorBody
  try {
    body = (await response.json()) as ApiErrorBody
  } catch {
    body = { code: 'REQUEST_FAILED', message: `请求失败（HTTP ${response.status}）` }
  }
  return new ApiClientError(response.status, body)
}
